import { makeAutoObservable, runInAction } from "mobx";
import {
  ColorBy,
  ColorByTreatment,
  ColorByType,
} from "../../map/models/ColorBy";
import {
  addCustomIcon,
  mapActive,
  MapboxStylesUrl,
  mapGradient,
  mapGradientEnd,
  mapGradientStart,
  mapGradientStep,
  mapUtils,
} from "../../map/utils/map-utils";
import { getAttributeValue } from "../../coding/utils/attribute-value";
import { AttributeDefinitions, AttributeType } from "@keita/lanepatrol-sdk";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNode } from "../../osaway/models/OsaNode";
import { PublicCredentials } from "../../auth/models/PublicCredentials";
import { setPasswordAuthAxios } from "../../shared/utils/network-utils";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import { OsaWay } from "../../osaway/models/OsaWay";
import { LayerSpecification, MapMouseEvent } from "mapbox-gl";
import { CustomSource } from "../../map/models/CustomSource";
import { OsaMapSource } from "../../map/sources/OsaMapSource";
import { HlaMapSource } from "../../map/sources/HlaMapSource";
import { PlannedTrajectoryMapSource } from "../../map/sources/PlannedTrajectoryMapSource";
import { PendingCoordChangesMapSource } from "../../map/sources/PendingCoordChangesMapSource";
import { ProjectsMapSource } from "../../map/sources/ProjectsMapSource";
import mapboxgl from "mapbox-gl";
import { StreetViewMapSource } from "../../map/sources/StreetViewMapSource";
import cameraIconSvg from "../../../assets/svg/camera-icon.svg";
import commentIconSvg from "../../../assets/svg/comment-icon.svg";
import chevronIconSvg from "../../../assets/svg/chevron-icon.svg";
import { QueryInfrastructureMapSource } from "../../map/sources/QueryInfrastructureMapSource";
import { ShiftDragSelection } from "../../map/sources/ShiftDragSelection";
import { HlaWay } from "../../hla/models/HlaWay";
import { MapillaryMapSource } from "../../map/sources/MapillaryMapSource";

export enum MapMode {
  HomePage = "HomePage",
  ProjectPage = "ProjectPage",
  QueryHlaOriginDestination = "QueryHlaOriginDestination",
  QueryHlaBbox = "QueryHlaBbox",
  ManageNodes = "ManageNodes",
  CraCoding = "CraCoding",
  PlanTrajectory = "PlanTrajectory",
  MapillaryImport = "MapillaryImport",
}

export enum MapPageView {
  Map,
  Dashboard,
}

export enum MapComponent {
  Detail = "detail",
  Filters = "filters",
  Legend = "legend",
  Shortcuts = "shortcuts",
  Comments = "comments",
}

export type MapComponentVisibility = Record<MapComponent, boolean>;

const defaultView = MapPageView.Map;

export class MapStore {
  rootStore;

  map?: mapboxgl.Map;
  mode = MapMode.HomePage;

  osaMapSource?: OsaMapSource;
  hlaMapSource?: HlaMapSource;
  plannedTrajectoryMapSource?: PlannedTrajectoryMapSource;
  pendingCoordChangesMapSource?: PendingCoordChangesMapSource;
  projectsMapSource?: ProjectsMapSource;
  streetViewMapSource?: StreetViewMapSource;
  queryInfrastructureMapSource?: QueryInfrastructureMapSource;
  shiftDragSelection?: ShiftDragSelection;
  mapillaryMapSource?: MapillaryMapSource;

  hideUnpublishedContent = false;

  colorBy: ColorBy = {
    type: ColorByType.Score,
    scoreKey: "global",
  };
  view = defaultView;
  componentVisibility: MapComponentVisibility = {
    detail: true,
    filters: true,
    legend: true,
    shortcuts: false,
    comments: true,
  };
  selectedStylesUrl = MapboxStylesUrl.Standard;
  mapResizeTrigger = 0;
  mapRecenterTrigger = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.view = defaultView;
      this.colorBy = {
        type: ColorByType.Score,
        scoreKey: "global",
      };
    });
  }

  setMode(mode: MapMode) {
    runInAction(() => {
      this.mode = mode;
    });

    this.projectsMapSource?.onModeChange(mode);
    this.hlaMapSource?.onModeChange(mode);
    this.osaMapSource?.onModeChange(mode);
    this.queryInfrastructureMapSource?.onModeChange(mode);
    this.plannedTrajectoryMapSource?.onModeChange(mode);
    this.shiftDragSelection?.onModeChange(mode);
    this.mapillaryMapSource?.onModeChange(mode);
  }

  isActive() {
    return mapActive(this.map);
  }

  init() {
    const map = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
      preserveDrawingBuffer: true,
    });

    map.on("load", () => {
      map.boxZoom.disable();
      map.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );

      this.addCustomIcons(map);
    });

    map.once("style.load", () => {
      map.resize();
      this.registerMap(map);
      this.initSources();
    });
  }

  initSources() {
    if (!this.map) return;

    this.projectsMapSource?.clear();
    this.projectsMapSource = new ProjectsMapSource(this.map, this.rootStore);
    this.hlaMapSource?.clear();
    this.hlaMapSource = new HlaMapSource(this.map, this.rootStore);
    this.osaMapSource?.clear();
    this.osaMapSource = new OsaMapSource(this.map, this.rootStore);
    this.plannedTrajectoryMapSource?.clear();
    this.plannedTrajectoryMapSource = new PlannedTrajectoryMapSource(
      this.map,
      this.rootStore,
    );
    this.pendingCoordChangesMapSource?.clear();
    this.pendingCoordChangesMapSource = new PendingCoordChangesMapSource(
      this.map,
      this.rootStore,
    );
    this.streetViewMapSource?.clear();
    this.streetViewMapSource = new StreetViewMapSource(this.map);
    this.queryInfrastructureMapSource?.clear();
    this.queryInfrastructureMapSource = new QueryInfrastructureMapSource(
      this.map,
      this.rootStore,
    );
    this.shiftDragSelection?.disable();
    this.shiftDragSelection = new ShiftDragSelection(this.map, this.rootStore);
    this.mapillaryMapSource?.clear();
    this.mapillaryMapSource = new MapillaryMapSource(this.map, this.rootStore);

    this.setMode(this.mode);
  }

  onSelectedOsaWayChange(osaWay?: OsaWay) {
    this.shiftDragSelection?.setSelectOsaNodes(
      osaWay?.lifeCycleStage === LifeCycleStage.DataCollection ||
        osaWay?.lifeCycleStage === LifeCycleStage.Coding,
    );
  }

  onSelectedHlaWayChange(hlaWay?: HlaWay) {
    this.shiftDragSelection?.setSelectHlaWays(!!hlaWay);
  }

  onSelectedMapillarySequenceChange(sequenceId?: string) {
    this.shiftDragSelection?.setSelectMapillaryPoints(!!sequenceId);
  }

  destroy() {
    this.plannedTrajectoryMapSource?.clear();
    this.plannedTrajectoryMapSource = undefined;

    this.osaMapSource?.clear();
    this.osaMapSource = undefined;

    this.hlaMapSource?.clear();
    this.hlaMapSource = undefined;

    this.pendingCoordChangesMapSource?.clear();
    this.pendingCoordChangesMapSource = undefined;

    this.projectsMapSource?.clear();
    this.projectsMapSource = undefined;

    this.queryInfrastructureMapSource?.disable();
    this.queryInfrastructureMapSource = undefined;

    this.mapillaryMapSource?.clear();
    this.mapillaryMapSource = undefined;

    this.map?.remove();
    this.map = undefined;
  }

  setComponentVisibility(
    setter: (prev: MapComponentVisibility) => MapComponentVisibility,
  ) {
    runInAction(() => {
      this.componentVisibility = setter(this.componentVisibility);
    });
  }

  recenterMap() {
    runInAction(() => {
      this.mapRecenterTrigger = this.mapRecenterTrigger + 1;
    });
  }

  centerMap() {
    const { projectStore } = this.rootStore;
    const selectedProject = projectStore.getSelected();

    if (mapActive(this.map)) {
      if (!selectedProject) {
        this.centerHomeMap();
      }
      mapUtils.centerMap(this.map!, (bounds) => {
        this.hlaMapSource?.extendBounds(bounds);
        this.osaMapSource?.extendBounds(bounds);
        this.plannedTrajectoryMapSource?.extendBounds(bounds);

        if (bounds.isEmpty() && selectedProject) {
          this.map?.flyTo({
            center: [selectedProject.longitude, selectedProject.latitude],
            zoom: 12,
          });
        }
      });
    }
  }

  refreshMapWindow() {
    runInAction(() => {
      this.mapResizeTrigger = this.mapResizeTrigger + 1;
    });
  }

  setView(view: MapPageView) {
    runInAction(() => {
      this.view = view;
    });
  }

  setColorBy(value: ColorBy) {
    runInAction(() => {
      this.colorBy = value;
    });

    const { filterStore } = this.rootStore;
    filterStore.filterNodes();
  }

  setCredentials(credentials: PublicCredentials) {
    runInAction(() => {
      this.hideUnpublishedContent = true;
    });
    setPasswordAuthAxios(credentials);
  }

  applyColorBySettings(node: OsaNode): string {
    const { filterStore } = this.rootStore;
    const disabledColor = "#fff";

    if (this.colorBy.type === ColorByType.Score) {
      if (!node.score) return disabledColor;

      const scoreEntry = node.score[this.colorBy.scoreKey];
      return scoreEntry.color;
    }

    if (this.colorBy.type === ColorByType.Treatment) {
      if (!node.treatments) return disabledColor;
      const colorBy = this.colorBy as ColorByTreatment;

      const treatmentFound = node.treatments.some(
        (t) => t.type === colorBy.treatmentType,
      );

      return treatmentFound ? mapGradientStart : mapGradientEnd;
    }

    if (this.colorBy.type === ColorByType.Attribute) {
      const allNodeCodingsByWayId =
        filterStore.originalOrForecastedAllNodeCodingsByWayId;
      const nodeCoding = allNodeCodingsByWayId.get(node.osaWayId)?.get(node.id);
      if (!nodeCoding) return disabledColor; // coding not available

      const attribute = AttributeDefinitions.getByName(
        this.colorBy.attributeName,
      )!;
      const value = getAttributeValue(attribute.name, nodeCoding.attributes);

      if (attribute.type === AttributeType.Select) {
        if (!value) return disabledColor;
        const length = attribute.options.length;
        const optionIndex = attribute.options.findIndex(
          (option) => option.value === value,
        );
        return mapGradientStep(length, optionIndex);
      } else if (attribute.type === AttributeType.TextArea) {
        if (!value) return mapGradientEnd;
        return mapGradientStart;
      } else if (attribute.type === AttributeType.Number) {
        if (isNaN(value as any)) return disabledColor;
        const limitedValue = Math.max(Math.min(value as number, 90), 0);
        return `#${mapGradient.hsvAt(limitedValue / 90).toHex()}`;
      }
    }

    return disabledColor;
  }

  registerMap(map: mapboxgl.Map) {
    runInAction(() => {
      this.map = map;
    });
  }

  zoomIn() {
    if (this.isActive()) {
      this.map?.zoomIn();
    }
  }

  zoomOut() {
    if (this.isActive()) {
      this.map?.zoomOut();
    }
  }

  centerHomeMap() {
    if (this.isActive()) {
      this.map?.flyTo({
        center: [5, 43],
        zoom: 3,
        speed: 5,
        pitch: 0,
        bearing: 0,
      });
    }
  }

  setMapStyle(style: MapboxStylesUrl) {
    this.selectedStylesUrl = style;

    const mapStyle = this.map?.getStyle();

    if (!this.map || !mapStyle) return;

    const customSources = Object.values(CustomSource);
    const savedSources = { ...mapStyle.sources };

    Object.entries(savedSources).forEach(([sourceName, spec]) => {
      if (!customSources.includes(sourceName as CustomSource)) {
        delete savedSources[sourceName];
      }
    });

    // Save the existing sources and layers
    const savedLayers = mapStyle.layers.reduce((list, layer) => {
      if (!customSources.includes(layer.source as CustomSource)) return list;
      return [...list, layer];
    }, [] as LayerSpecification[]);

    // apply the new style, which will delete all existing data
    this.map.setStyle(this.selectedStylesUrl);

    // re-add all the saved sources and layers
    this.map.once("styledata", () => {
      this.addCustomIcons(this.map!);

      // Add back the saved sources
      Object.keys(savedSources).forEach((sourceId) => {
        this.map?.addSource(sourceId, savedSources[sourceId]);
      });

      // Add back the saved layers
      savedLayers.forEach((layer) => {
        this.map?.addLayer(layer);
      });
    });
  }

  listenForClick(callback: (e: MapMouseEvent) => void): () => void {
    var onCancel = () => {};

    if (this.isActive()) {
      const canvas = this.map!.getCanvas();
      canvas.style.cursor = "pointer";

      const callAndCleanup = (e: MapMouseEvent) => {
        callback(e);
        canvas.style.cursor = "";
      };

      this.map?.once("click", callAndCleanup);

      onCancel = () => {
        this.map?.off("click", callAndCleanup);
        canvas.style.cursor = "";
      };
    }

    return onCancel;
  }

  private addCustomIcons(map: mapboxgl.Map) {
    addCustomIcon(map, "camera-icon", cameraIconSvg, 30);
    addCustomIcon(map, "comment-icon", commentIconSvg, 24, true);
    addCustomIcon(map, "chevron-icon", chevronIconSvg, 24, true);
  }
}
